import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { 
  AuthProvider, 
  LanguageProvider, 
  OfflineProvider, 
  ThemeProvider,
  useAuth 
} from './context/AppContext';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { DashboardLayout } from './components/Layout';
import { DashboardPage } from './pages/DashboardPage';
import { CoursesPage } from './pages/CoursesPage';
import { GradesPage } from './pages/GradesPage';
import { AIAssistantPage } from './pages/AIAssistantPage';
import { MessagesPage } from './pages/MessagesPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { UniversitiesPage } from './pages/UniversitiesPage';
import { ProfilePage } from './pages/ProfilePage';
import { UsersPage } from './pages/UsersPage';
import { FacultiesPage } from './pages/FacultiesPage';
import { LibraryPage } from './pages/LibraryPage';
import { ReferralPage } from './pages/ReferralPage';
import { AudioCallsPage } from './pages/AudioCallsPage';
import { StudyGroupsPage } from './pages/StudyGroupsPage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { SchedulePage } from './pages/SchedulePage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { DeliberationPage } from './pages/DeliberationPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { OnlineCoursesPage } from './pages/OnlineCoursesPage';
import { AssignmentsPage } from './pages/AssignmentsPage';
import { AcademicYearPage } from './pages/AcademicYearPage';
import { DictionaryPage } from './pages/DictionaryPage';
import { TransportPage } from './pages/TransportPage';
import { RolesPage } from './pages/RolesPage';
import { RegistrationsPage } from './pages/RegistrationsPage';
import { CoAdminsPage } from './pages/CoAdminsPage';
import { LegalPage } from './pages/LegalPage';
import { LandingPage } from './pages/LandingPage';
import { OrganizationPage } from './pages/OrganizationPage';
import { InstitutionWizardPage } from './pages/InstitutionWizardPage';
import { MyDocumentsPage } from './pages/MyDocumentsPage';
import { TeacherProfilePage } from './pages/TeacherProfilePage';
import { ClassGroupsPage } from './pages/ClassGroupsPage';
import { MyClassPage } from './pages/MyClassPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { SupportPage } from './pages/SupportPage';
import { CollaborationInboxPage } from './pages/CollaborationInboxPage';
import { GuidePage } from './pages/GuidePage';
import './index.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--tenant-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-[var(--tenant-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/legal" element={<LegalPage />} />

      {/* Protected Routes - All Users */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/courses" element={
        <ProtectedRoute>
          <CoursesPage />
        </ProtectedRoute>
      } />
      <Route path="/grades" element={
        <ProtectedRoute>
          <GradesPage />
        </ProtectedRoute>
      } />
      <Route path="/ai-assistant" element={
        <ProtectedRoute>
          <AIAssistantPage />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/referral" element={
        <ProtectedRoute>
          <ReferralPage />
        </ProtectedRoute>
      } />
      <Route path="/library" element={
        <ProtectedRoute>
          <LibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/calls" element={
        <ProtectedRoute>
          <AudioCallsPage />
        </ProtectedRoute>
      } />
      <Route path="/study-groups" element={
        <ProtectedRoute>
          <StudyGroupsPage />
        </ProtectedRoute>
      } />

      {/* Student Routes */}
      <Route path="/payments" element={
        <ProtectedRoute allowedRoles={['student', 'super_admin', 'co_admin', 'rector', 'vice_rector_academic', 'sg_academic', 'sg_admin', 'budget_director', 'admin_staff']}>
          <PaymentsPage />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/universities" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin']}>
          <SuperAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/schedule" element={
        <ProtectedRoute>
          <SchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin', 'rector', 'vice_rector_academic', 'sg_academic', 'sg_admin', 'budget_director', 'dean', 'department_head']}>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/deliberations" element={
        <ProtectedRoute allowedRoles={['super_admin', 'rector', 'vice_rector_academic', 'sg_academic', 'dean', 'department_head', 'teacher', 'student']}>
          <DeliberationPage />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/online-courses" element={
        <ProtectedRoute>
          <OnlineCoursesPage />
        </ProtectedRoute>
      } />
      <Route path="/assignments" element={
        <ProtectedRoute>
          <AssignmentsPage />
        </ProtectedRoute>
      } />
      <Route path="/academic-year" element={
        <ProtectedRoute>
          <AcademicYearPage />
        </ProtectedRoute>
      } />
      <Route path="/dictionary" element={
        <ProtectedRoute>
          <DictionaryPage />
        </ProtectedRoute>
      } />
      <Route path="/transport" element={
        <ProtectedRoute>
          <TransportPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin', 'rector', 'vice_rector_academic', 'sg_academic', 'sg_admin', 'dean', 'department_head', 'admin_staff', 'academic_secretary', 'registrar', 'it_admin', 'librarian', 'faculty_secretary', 'section_head', 'dept_secretary', 'vice_dean', 'sg_research', 'receptionist', 'protocol_chief', 'apparitor', 'cashier']}>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/roles" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin', 'rector', 'sg_admin', 'it_admin']}>
          <RolesPage />
        </ProtectedRoute>
      } />
      <Route path="/registrations" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin', 'rector', 'sg_academic', 'registrar', 'academic_secretary', 'faculty_secretary']}>
          <RegistrationsPage />
        </ProtectedRoute>
      } />
      <Route path="/co-admins" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <CoAdminsPage />
        </ProtectedRoute>
      } />
      <Route path="/organization" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin', 'rector', 'sg_admin', 'sg_academic', 'it_admin', 'dean']}>
          <OrganizationPage />
        </ProtectedRoute>
      } />
      <Route path="/new-institution" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin']}>
          <InstitutionWizardPage />
        </ProtectedRoute>
      } />
      <Route path="/my-documents" element={
        <ProtectedRoute>
          <MyDocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher-profile" element={
        <ProtectedRoute allowedRoles={['teacher', 'visiting_teacher', 'super_admin', 'co_admin']}>
          <TeacherProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/class-groups" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin', 'rector', 'sg_academic', 'dean', 'department_head', 'admin_staff']}>
          <ClassGroupsPage />
        </ProtectedRoute>
      } />
      <Route path="/my-class" element={
        <ProtectedRoute>
          <MyClassPage />
        </ProtectedRoute>
      } />
      <Route path="/announcements" element={
        <ProtectedRoute>
          <AnnouncementsPage />
        </ProtectedRoute>
      } />
      <Route path="/faculties" element={
        <ProtectedRoute allowedRoles={['super_admin', 'rector', 'dean', 'admin_staff']}>
          <FacultiesPage />
        </ProtectedRoute>
      } />
      <Route path="/departments" element={
        <ProtectedRoute allowedRoles={['super_admin', 'rector', 'dean', 'department_head', 'admin_staff']}>
          <FacultiesPage />
        </ProtectedRoute>
      } />
      <Route path="/programs" element={
        <ProtectedRoute allowedRoles={['super_admin', 'rector', 'dean', 'department_head', 'admin_staff']}>
          <FacultiesPage />
        </ProtectedRoute>
      } />

      {/* Default Routes */}
      <Route path="/support" element={
        <ProtectedRoute>
          <SupportPage />
        </ProtectedRoute>
      } />
      <Route path="/collaboration-inbox" element={
        <ProtectedRoute allowedRoles={['super_admin', 'co_admin']}>
          <CollaborationInboxPage />
        </ProtectedRoute>
      } />
      <Route path="/guide" element={
        <ProtectedRoute>
          <GuidePage />
        </ProtectedRoute>
      } />
      <Route path="/" element={<LandingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <OfflineProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </OfflineProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
